/**
 * @typedef {import('../AabSelect.js').Option} Option
 */

/**
 * @typedef {import('../AabSelect.js').Group} Group
 */

/**
 * @typedef {object} SearchResult
 * @property {number} index
 * @property {?Option} option
 */

/**
 * @typedef {object} GroupedSearchResult
 * @property {number} groupIndex
 * @property {number} optionIndex
 * @property {?Option} option
 */

/**
 * Returns the 'next' non-disabled option (and it's index) from an array of
 * options
 *
 * @param {number} startIndex
 * @param {Option[]} options
 * @returns {SearchResult}
 */
export function findNextOption(startIndex, options) {
  let index = startIndex;
  let option;

  do {
    index += 1;
    if (index > options.length - 1) {
      index = 0;
    }
    option = options[index];
  } while (option.disabled && index !== startIndex);

  return { index, option };
}

/**
 * Returns the 'next' non-disabled grouped option (and it's group and option index) from an array of
 * groups
 *
 * @param {number} groupIndex
 * @param {number} optionIndex
 * @param {Group[]} groups
 * @returns {GroupedSearchResult}
 */
export function findNextGroupedOption(groupIndex, optionIndex, groups) {
  let i = groupIndex;
  let j = optionIndex;
  let option;

  do {
    do {
      j += 1;
      if (i >= groups.length - 1 && j > groups[i].options.length - 1) {
        i = 0;
        j = 0;
      }
      if (j > groups[i].options.length - 1) {
        i += 1;
        j = 0;
      }
      option = groups[i].options[j];
    } while ((option.disabled) && j !== optionIndex);
  } while ((!option));

  return { groupIndex: i, optionIndex: j, option: groups[i].options[j] };
}

/**
 * Returns the 'previous' non-disabled option (and it's index) from an array of
 * options
 *
 * @param {number} startIndex
 * @param {Option[]} options
 * @returns {SearchResult}
 */
export function findPreviousOption(startIndex, options) {
  let index = startIndex;
  let option;

  do {
    index -= 1;
    if (index < 0) {
      index = options.length - 1;
    }
    option = options[index];
  } while ((option.disabled) && index !== startIndex);

  return { index, option };
}

/**
 * Returns the 'previous' non-disabled grouped option (and it's group and option index) from an array of
 * groups
 *
 * @param {number} groupIndex
 * @param {number} optionIndex
 * @param {Group[]} groups
 * @returns {GroupedSearchResult}
 */
export function findPreviousGroupedOption(groupIndex, optionIndex, groups) {
  let i = groupIndex;
  let j = optionIndex;
  let option;

  do {
    do {
      j -= 1;
      if (i <= 0 && j < 0) {
        i = groups.length - 1;
        j = groups[i].options.length - 1;
      }
      if (j < 0) {
        i -= 1;
        j = groups[i].options.length - 1;
      }
      option = groups[i].options[j];
    } while (option.disabled && j !== optionIndex);
  } while ((!option));

  return { groupIndex: i, optionIndex: j, option: groups[i].options[j] };
}

/**
 * Returns the first non-disabled option (and it's index) from an array of
 * options
 *
 * @param {Option[]} options
 * @returns {SearchResult}
 */
export function findFirstOption(options) {
  const index = options.findIndex((option) => !option.disabled);
  if (index < 0) {
    return { index, option: null };
  }
  return { index, option: options[index] };
}

/**
 * Returns the first non-disabled grouped option (and it's group and option index) from an array of
 * groups
 *
 * @param {Group[]} groups
 * @returns {GroupedSearchResult}
 */
export function findFirstGroupedOption(groups) {
  const [groupIndex, optionIndex] = groups.flatMap((group, currentGroupIndex) => {
    const foundOptionIndex = group.options.findIndex((option) => !option.disabled);
    return foundOptionIndex > -1 ? [currentGroupIndex, foundOptionIndex] : [-1, -1];
  });

  if (groupIndex < 0 || optionIndex < 0) {
    return { groupIndex, optionIndex, option: null };
  }
  return { groupIndex, optionIndex, option: groups[groupIndex].options[optionIndex] };
}

/**
 * Returns the last non-disabled option (and it's index) from an array of
 * options
 *
 * @param {Option[]} options
 * @returns {SearchResult}
 */
export function findLastOption(options) {
  let i;
  let option;

  for (i = options.length - 1; i > -1; i -= 1) {
    option = options[i];
    if (!option.disabled) {
      break;
    }
  }
  if (i < 0) {
    return { index: i, option: null };
  }
  return { index: i, option: options[i] };
}

/**
 * Returns the last non-disabled grouped option (and it's group and option index) from an array of
 * groups
 *
 * @param {Group[]} groups
 * @returns {GroupedSearchResult}
 */
export function findLastGroupedOption(groups) {
  let i;
  let j;
  let option;

  for (i = 0; i <= groups.length - 1; i += 1) {
    for (j = groups[i].options.length - 1; j > -1; j -= 1) {
      option = groups[i].options[j];
      if (!option.disabled) {
        break;
      }
    }
    if (i === groups.length - 1) {
      break;
    }
  }
  if (j < 0) {
    return { groupIndex: -1, optionIndex: -1, option: null };
  }
  return { groupIndex: i, optionIndex: j, option: groups[i].options[j] };
}
